import { serverResponse } from "../util/fakeServer";

const AUTH_TOKENS_KEY = "inpublish_auth_tokens";

// ===== CONFIG =====

// Base URL statica delle API v2: uguale per tutti i tenant, cambia solo per
// ambiente (test/prod) via .env (REACT_APP_API_V2_URL). Richiede rebuild al
// cambio. Il dato per-tenant resta solo l'X-Client-Token (da publication.json).
const API_BASE_URL =
  process.env.REACT_APP_API_V2_URL;
//console.log(API_BASE_URL);
const getBaseUrl = () => API_BASE_URL;
const getClientToken = () => serverResponse.config.x_client_token || "";

// ===== ENDPOINTS =====

export const ENDPOINTS = {
  // Auth
  LOGIN: "/auth/login",
  LOGOUT: "/auth/logout",
  REFRESH_TOKEN: "/auth/refresh",
  // Platform User
  SIGNUP: "/platform-user/signup",
  UPDATE_PROFILE: "/platform-user/update",
  SET_PASSWORD: "/platform-user/set-password",
  REQUEST_PASSWORD_RESET: "/platform-user/request-password-reset",
  RESET_PASSWORD: "/platform-user/reset-password",
  DELETE_ACCOUNT: "/platform-user/remove-me",
  // Orders
  GET_ORDERS: "/ecommerce/get-orders",
  GET_ORDER: "/ecommerce/get-order",
  // Favorites
  SAVE_LIST: "/lists/save-list",
  ADD_PRODUCT_TO_LIST: "/lists/add-product",
  REMOVE_PRODUCT_FROM_LIST: "/lists/remove-product",
  GET_LIST_PRODUCTS: "/lists/get-list-products",
  // Catalog / Prizes
  GET_ACTIVE_CATALOG: "/catalog/get-active-catalog",
  GET_PRIZES: "/catalog/get-prizes",
  CHECK_POINTS: "/catalog/check-points",
  CREATE_PRIZE_ORDER: "/catalog/create-order",
  GET_PRIZE_ORDERS: "/catalog/get-orders",
  // Support
  SEND_MAIL: "/mailer/send-mail",
  // Checkout / Ecommerce
  SAVE_CART: "/ecommerce/save-cart",
  CLEAR_CART: "/ecommerce/clear-cart",
  CHECK_CART: "/ecommerce/check-cart",
  CONFIRM_CART: "/ecommerce/confirm-cart",
  GET_SLOTS: "/ecommerce/get-slots",
  GET_SHIPPING_COST: "/ecommerce/get-shipping-cost",
  GET_MARKETS: "/ecommerce/get-markets",
  // Stripe embedded checkout (path da confermare col backend)
  CHECKOUT_CREATE: "/ecommerce/create-checkout",
  // Metodi di pagamento dinamici
  PAYMENT_METHODS: "/ecommerce/payment-methods",
  // Azienda / Config
  GET_INFO: "/azienda/get-info",
};

// ===== HEADERS =====

const getPublicHeaders = () => ({
  "Content-Type": "application/json",
  "X-Client-Token": getClientToken(),
});

const getAuthHeaders = (token) => ({
  "Content-Type": "application/json",
  "X-Client-Token": getClientToken(),
  Authorization: `Bearer ${token}`,
});

// ===== TOKEN MANAGEMENT =====

export const getStoredTokens = () => {
  try {
    const stored = localStorage.getItem(AUTH_TOKENS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const storeTokens = (tokens) => {
  try {
    localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(tokens));
  } catch { /* ignore */ }
};

export const clearTokens = () => {
  try {
    localStorage.removeItem(AUTH_TOKENS_KEY);
  } catch { /* ignore */ }
};

const refreshTokenRequest = async (refreshToken) => {
  const res = await fetch(`${getBaseUrl()}${ENDPOINTS.REFRESH_TOKEN}`, {
    method: "POST",
    headers: getPublicHeaders(),
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) throw new Error("Refresh failed");
  return res.json();
};

// ===== FETCH WITH AUTH (auto-refresh on 401) =====

export const fetchWithAuth = async (endpoint, options = {}) => {
  const tokens = getStoredTokens();
  if (!tokens?.jwtToken) throw new Error("Not authenticated");

  const url = `${getBaseUrl()}${endpoint}`;
  const config = {
    ...options,
    headers: {
      ...getAuthHeaders(tokens.jwtToken),
      ...(options.headers || {}),
    },
  };

  let res = await fetch(url, config);

  // On 401, try refresh
  if (res.status === 401 && tokens.refreshToken) {
    try {
      const refreshData = await refreshTokenRequest(tokens.refreshToken);
      if (refreshData.status === 200 && refreshData.data) {
        const newTokens = {
          jwtToken: refreshData.data.jwt_token,
          refreshToken: refreshData.data.refresh_token,
          expiresAt: Date.now() + refreshData.data.expires_in * 1000,
        };
        storeTokens(newTokens);

        // Retry with new token
        config.headers = {
          ...getAuthHeaders(newTokens.jwtToken),
          ...(options.headers || {}),
        };
        res = await fetch(url, config);
      } else {
        clearTokens();
        throw new Error("Token refresh failed");
      }
    } catch {
      clearTokens();
      throw new Error("Token refresh failed");
    }
  }

  return res.json();
};

// ===== PUBLIC API CALLS =====

export const publicFetch = async (endpoint, body = {}) => {
  const url = `${getBaseUrl()}${endpoint}`;
  const res = await fetch(url, {
    method: "POST",
    headers: getPublicHeaders(),
    body: JSON.stringify(body),
  });
  return res.json();
};

// ===== AUTH SERVICES =====

export const loginApi = (email, password) =>
  publicFetch(ENDPOINTS.LOGIN, { email, password });

export const signupApi = (data) =>
  publicFetch(ENDPOINTS.SIGNUP, data);

export const logoutApi = (token, refreshToken) =>
  fetch(`${getBaseUrl()}${ENDPOINTS.LOGOUT}`, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).then((r) => r.json());

export const requestPasswordResetApi = (email, url, home) =>
  publicFetch(ENDPOINTS.REQUEST_PASSWORD_RESET, { email, url, home });

export const resetPasswordApi = (reset_token, password) =>
  publicFetch(ENDPOINTS.RESET_PASSWORD, { reset_token, password });

// ===== PROFILE SERVICES =====

export const updateProfileApi = (data) =>
  fetchWithAuth(ENDPOINTS.UPDATE_PROFILE, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const setPasswordApi = (newPassword) =>
  fetchWithAuth(ENDPOINTS.SET_PASSWORD, {
    method: "PUT",
    body: JSON.stringify({ newP: newPassword }),
  });

export const deleteAccountApi = () =>
  fetchWithAuth(ENDPOINTS.DELETE_ACCOUNT, {
    method: "POST",
  });

// ===== ORDERS SERVICES =====

export const getOrdersApi = (page = 1, pageSize = 10) =>
  fetchWithAuth(ENDPOINTS.GET_ORDERS, {
    method: "POST",
    body: JSON.stringify({ page, page_size: pageSize }),
  });

export const getOrderDetailApi = (id, numeroOrdine) =>
  fetchWithAuth(ENDPOINTS.GET_ORDER, {
    method: "POST",
    body: JSON.stringify({ id, numero_ordine: numeroOrdine }),
  });

// ===== FAVORITES SERVICES =====

export const saveListApi = (name) =>
  fetchWithAuth(ENDPOINTS.SAVE_LIST, {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const addProductToListApi = (idList, idProduct, quantity, variety) =>
  fetchWithAuth(ENDPOINTS.ADD_PRODUCT_TO_LIST, {
    method: "POST",
    body: JSON.stringify({ id_list: idList, id_product: idProduct, quantity, variety }),
  });

export const removeProductFromListApi = (idList, idProduct) =>
  fetchWithAuth(ENDPOINTS.REMOVE_PRODUCT_FROM_LIST, {
    method: "POST",
    body: JSON.stringify({ id_list: idList, id_product: idProduct }),
  });

export const getListProductsApi = (idList, idClient, idPv) =>
  fetchWithAuth(ENDPOINTS.GET_LIST_PRODUCTS, {
    method: "POST",
    body: JSON.stringify({ id_list: idList, id_client: idClient, id_pv: idPv }),
  });

// ===== PRIZES SERVICES =====

export const getActiveCatalogApi = () =>
  publicFetch(ENDPOINTS.GET_ACTIVE_CATALOG, {});

export const getPrizesApi = (idCatalogo, page = 1, pageSize = 10) =>
  publicFetch(ENDPOINTS.GET_PRIZES, {
    id_catalogo: idCatalogo,
    page,
    page_size: pageSize,
  });

export const checkPointsApi = (cardCode, idCatalogo) =>
  fetchWithAuth(ENDPOINTS.CHECK_POINTS, {
    method: "POST",
    body: JSON.stringify({ card_code: cardCode, id_catalogo: idCatalogo }),
  });

export const createPrizeOrderApi = (data) =>
  fetchWithAuth(ENDPOINTS.CREATE_PRIZE_ORDER, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getPrizeOrdersApi = (idUtente, page = 1, pageSize = 10) =>
  fetchWithAuth(ENDPOINTS.GET_PRIZE_ORDERS, {
    method: "POST",
    body: JSON.stringify({ id_utente: idUtente, page, page_size: pageSize }),
  });

// ===== SUPPORT SERVICES =====

export const sendMailApi = (data) =>
  publicFetch(ENDPOINTS.SEND_MAIL, data);

// ===== CHECKOUT / ECOMMERCE SERVICES =====

// Helper: try authenticated, fallback to public
const fetchAuthOrPublic = async (endpoint, data) => {
  const tokens = getStoredTokens();
  if (tokens?.jwtToken) {
    return fetchWithAuth(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
  return publicFetch(endpoint, data);
};

export const saveCartApi = (data) => fetchAuthOrPublic(ENDPOINTS.SAVE_CART, data);

export const clearCartApi = () => fetchAuthOrPublic(ENDPOINTS.CLEAR_CART, {});

export const checkCartApi = (data) => fetchAuthOrPublic(ENDPOINTS.CHECK_CART, data);

export const confirmCartApi = (data) => fetchAuthOrPublic(ENDPOINTS.CONFIRM_CART, data);

export const getSlotsApi = (storeId, date) =>
  publicFetch(ENDPOINTS.GET_SLOTS, {
    id_market: String(storeId),
    day: date,
    delivery: 0,
  });

export const getShippingCostApi = (storeId, cap, value) =>
  publicFetch(ENDPOINTS.GET_SHIPPING_COST, {
    store_id: storeId,
    cap,
    value,
  });

export const getMarketsApi = () =>
  publicFetch(ENDPOINTS.GET_MARKETS, {});

// Config e-commerce dell'azienda (azienda/get-info -> data.settings).
// Chiamata pubblica: basta X-Client-Token, che scopa gia' il tenant corrente.
export const getInfoApi = () =>
  publicFetch(ENDPOINTS.GET_INFO, {});

// Crea la sessione Stripe embedded. Il backend risolve il carrello dall'id_param_url
// (persistito dal write) e ritorna { client_secret, publishable_key }.
export const createCheckoutApi = (data) =>
  fetchAuthOrPublic(ENDPOINTS.CHECKOUT_CREATE, data);

// Metodi di pagamento abilitati per il tenant (dinamici da backend).
export const getPaymentMethodsApi = () =>
  fetchAuthOrPublic(ENDPOINTS.PAYMENT_METHODS, {});
